// components/CandleChart.js
import React, { useEffect, useRef } from 'react';
import { createChart, ColorType, CandlestickSeries } from 'lightweight-charts';

export const CandleChart = ({ data, theme, symbol }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();
  const seriesRef = useRef();

  // 1. Initialize Chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: theme.cardBg },
        textColor: theme.textSecondary,
      },
      grid: {
        vertLines: { color: theme.isDark ? '#262626' : '#e5e7eb' },
        horzLines: { color: theme.isDark ? '#262626' : '#e5e7eb' },
      },
      width: chartContainerRef.current.clientWidth,
      height: 650,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 12, // Leave some space at the end
        barSpacing: 12,  // Default zoom level
      },
      crosshair: {
        mode: 1, // Magnet mode
      },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#00bfa5',
      downColor: '#ff5253',
      borderVisible: false,
      wickUpColor: '#00bfa5',
      wickDownColor: '#ff5253',
    });

    if (data && data.length > 0) {
      series.setData(data);
    }

    chartRef.current = chart;
    seriesRef.current = series;

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, []);

  // 2. Theme Handling
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.applyOptions({
        layout: {
          background: { type: ColorType.Solid, color: theme.cardBg },
          textColor: theme.textSecondary,
        },
        grid: {
          vertLines: { color: theme.isDark ? '#262626' : '#e5e7eb' },
          horzLines: { color: theme.isDark ? '#262626' : '#e5e7eb' },
        },
      });
    }
  }, [theme]);

  // 3. Real-Time Data Updates
  useEffect(() => {
    if (!seriesRef.current || data.length === 0) return;

    const currentData = seriesRef.current.data();
    const lastInternalCandle = currentData.length > 0 
      ? currentData[currentData.length - 1] 
      : null;
    const lastIncomingCandle = data[data.length - 1];

    if (!lastInternalCandle) {
        seriesRef.current.setData(data);
        return;
    }

    if (lastIncomingCandle.time === lastInternalCandle.time) {
        seriesRef.current.update(lastIncomingCandle);
    } 
    else if (lastIncomingCandle.time > lastInternalCandle.time) {
        seriesRef.current.update(lastIncomingCandle);
    } else {
        seriesRef.current.setData(data);
    }
  }, [data]);

  // --- UX ACTIONS ---

  const handleZoomIn = () => {
    if (chartRef.current) {
      const timeScale = chartRef.current.timeScale();
      // Increase bar spacing to zoom in
      timeScale.applyOptions({
        barSpacing: timeScale.options().barSpacing * 1.2, 
      });
    }
  };

  const handleZoomOut = () => {
    if (chartRef.current) {
      const timeScale = chartRef.current.timeScale();
      // Decrease bar spacing to zoom out
      timeScale.applyOptions({
        barSpacing: timeScale.options().barSpacing * 0.8,
      });
    }
  };

  const handleReset = () => {
    if (chartRef.current) {
      // 1. Reset Time Scale (Fit Horizontal)
      chartRef.current.timeScale().fitContent();
      
      // 2. Reset Price Scale (Auto Scale Vertical)
      // This forces the price scale to look at the currently visible data
      chartRef.current.priceScale('right').applyOptions({
         autoScale: true,
      });
    }
  };

  const handleAutoScale = () => {
    if (chartRef.current) {
        // Toggle auto scale
        const priceScale = chartRef.current.priceScale('right');
        // Currently Lightweight charts is "Auto" by default if you don't manually scroll.
        // We force it back to auto mode.
        priceScale.applyOptions({
            autoScale: true,
        });
        // Scroll to the real-time edge
        chartRef.current.timeScale().scrollToRealTime();
    }
  };

  // Styles for the toolbar buttons
  const btnStyle = {
    background: theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
    border: `1px solid ${theme.isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
    color: theme.textSecondary,
    borderRadius: '4px',
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    backdropFilter: 'blur(4px)',
    transition: 'all 0.2s',
  };

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      {/* CHART CONTAINER */}
      <div ref={chartContainerRef} style={{ width: '100%', height: '100%' }} />

      {/* FLOATING TOOLBAR */}
      <div style={{
        position: 'absolute',
        top: '12px',
        right: '12px',
        zIndex: 20,
        display: 'flex',
        gap: '6px',
      }}>
        <button 
            onClick={handleZoomIn} 
            style={btnStyle}
            title="Zoom In (+)"
        >
            +
        </button>
        <button 
            onClick={handleZoomOut} 
            style={btnStyle}
            title="Zoom Out (-)"
        >
            -
        </button>
        <button 
            onClick={handleAutoScale} 
            style={btnStyle}
            title="Auto Scale (Focus Latest)"
        >
            Auto
        </button>
        <button 
            onClick={handleReset} 
            style={btnStyle}
            title="Reset View (Fit All)"
        >
            Reset
        </button>
      </div>
    </div>
  );
};